import {
    CircularProgress,
    Dialog,
    makeStyles,
    Tooltip
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { getFormattedDate, getReportTypeText } from '../../../utils'

import CriticalIcon from '@material-ui/icons/HighlightOff'
import WarningIcon from '@material-ui/icons/ErrorOutline'
import OkIcon from '@material-ui/icons/CheckCircleOutline'
import { useBooleanState } from '../../../utils/hooks/useBooleanState'
import { Button } from '../../root/components/common'
import { DialogOpener } from '../../root/components/common/dialog-opener'
import { ReportForm } from './report-form'
import { TOM_CONTENT_TYPES } from '../constants'
import { useMakeReport } from '../hooks/useMakeReport'
import { getReportSeverity } from '../utils/getReportSeverity'
import { getSeverityColorIcon } from '../utils/getSeverity'
import { useSelector } from 'react-redux'
import getTheme from '../../../config/theme-config'
import { OperationModeIcon } from '../../root/components/common/operation-mode-icon'
import { getLocationPath } from '../../../utils/get-location-path'
import { OPERATION_MODES } from '../../root/constants'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'
import { UserPermissions } from 'web/src/services/permissions'

const AI_ICON = require('../../../assets/img/tom/misc/ai.png')
const USER_PROCESSED_ICON = require('../../../assets/img/tom/misc/userProcessed.png')

const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
        padding: '16px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '@media (min-width: 1200px)': {
            flexDirection: 'row'
        }
    },
    image: {
        maxWidth: 400,
        padding: '0 36px',
        '@media (min-width: 1200px)': {
            padding: '0'
        },
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        '&& > img': {
            width: '100%'
        }
    },
    info: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 16px',
        minWidth: 380,
        flex: 1,
        width: '100%',
        // height: 400,
        '@media (min-width: 1200px)': {
            // height: 300,
        }
    },
    detailsRow: {
        display: 'flex',
        marginBottom: 10,
        alignItems: 'center'
    },
    detailsNameColumn: {
        flex: 1,
        color: theme.quaternary,
        fontWeight: 'bold',
        minWidth: 140,
        '@media (min-width: 1200px)': {
            minWidth: 80
        }
    },
    detailsValueColumn: {
        flex: 1,
        display: 'flex',
        alignItems: 'center'
    },
    actionsRow: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: 'auto',
        '&& > button': {
            marginRight: 8,
            paddingLeft: 24,
            paddingRight: 24
        }
    },
    reviceBtn: {
        marginTop: 'auto',
        zIndex: 2
    },
    flexRow: {
        display: 'flex',
        alignItems: 'center',
        '&& > span': {
            marginRight: 8
        }
    },
    iconsBlock: {
        position: 'absolute',
        right: 8,
        top: 8,
        width: 36,
        display: 'flex',
        flexDirection: 'column',
        '&& > img:not(:last-child)': {
            marginBottom: 8
        }
    },
    tooltip: {
        fontSize: 15
    },
    backdrop: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        background: '#FFF',
        opacity: 0.6,
        zIndex: 1
    }
}))

const ITEM_STATUSES = {
    not_processed: 'not_processed',
    processed: 'processed',
    revise: 'revise'
}

export const TomImageItem = ({ item, notProcessedStatuses, queryKey }) => {
    const cls = useStyles()
    const theme = useSelector(getTheme)
    const { locale } = useSelector(store => store.main)
    const loggedInUser = useSelector(state => state.main.user)

    const {
        id,
        status,
        created_at,
        report_type,
        content_type,
        location_path_array,
        user_name,
        report_data,
        is_evaluation_required,
        customer_id,
        device: { operation_mode, customer_name, name }
    } = item

    const canUpdate =
        (UserPermissions.device(customer_id).update &&
            UserPermissions.report(customer_id).create &&
            item.device.is_assigned) ||
        (UserPermissions.isAllOriginalGranted() &&
            customer_id === loggedInUser.original_customer_id)

    const lightbox = useBooleanState(false)
    const [itemProcessStatus, setItemProcessStatus] = useState(
        ITEM_STATUSES.not_processed
    )
    const [isEvaluationRequired, setIsEvaluationRequired] = useState(
        is_evaluation_required
    )

    const notProcessed = notProcessedStatuses.includes(status)
    //temporarily all pictures marked with AI
    // const isAI = status === "ai_processed";
    const isAI = true

    const fileLink = 'data:image/jpeg;base64,' + item.file

    const { isLoading, mutateAsync } = useMakeReport(queryKey)

    const reportSeverity = getReportSeverity(report_type, status)
    const { icon: Icon, color } = getSeverityColorIcon(reportSeverity, theme)

    const imageProcessed = itemProcessStatus === ITEM_STATUSES.processed

    const handleMakeOkReport = async () => {
        await mutateAsync({
            id: id,
            body: {
                content_type: TOM_CONTENT_TYPES.empty,
                operation_mode: item.device.operation_mode
            }
        })
        setItemProcessStatus(ITEM_STATUSES.processed)
    }

    const hasAdditionalTomData = report_data && report_data.tom_data
    const operationMode =
        hasAdditionalTomData && report_data.tom_data.operation_mode
            ? report_data.tom_data.operation_mode
            : operation_mode

    const isInsectMode = operationMode === OPERATION_MODES.insect

    useEffect(() => {
        setIsEvaluationRequired(is_evaluation_required)
    }, [is_evaluation_required])

    return (
        <div className={cls.root}>
            <div className={cls.iconsBlock}>
                {isAI && !isInsectMode && (
                    <Tooltip
                        classes={{ tooltip: cls.tooltip }}
                        title={locale.tom.aiProcessed}
                    >
                        <img src={AI_ICON} />
                    </Tooltip>
                )}
                {!notProcessed && (
                    <Tooltip
                        classes={{ tooltip: cls.tooltip }}
                        title={locale.tom.userProcessed}
                    >
                        <img src={USER_PROCESSED_ICON} />
                    </Tooltip>
                )}
            </div>
            <div className={cls.image}>
                <img onClick={() => lightbox.on()} src={fileLink} />
                {lightbox.is && (
                    <Lightbox
                        animationDuration={200}
                        reactModalStyle={{ overlay: { zIndex: 3000 } }}
                        mainSrc={fileLink}
                        onCloseRequest={lightbox.off}
                    />
                )}
            </div>
            <div className={cls.info}>
                <InfoRow label={locale.tom.deviceName} value={name} />
                <InfoRow label={locale.tom.companyName} value={customer_name} />
                <InfoRow
                    label={locale.locations.locationsPath}
                    value={getLocationPath(location_path_array)}
                />
                {content_type && content_type !== 'unknown' && (
                    <InfoRow
                        label={locale.tom.contentType}
                        value={
                            content_type !== 'empty'
                                ? locale.tom[content_type] || content_type
                                : ' - '
                        }
                    />
                )}
                {hasAdditionalTomData &&
                    report_data.tom_data.infestation_amount && (
                        <InfoRow
                            label={locale.tom.amount}
                            value={report_data.tom_data.infestation_amount}
                        />
                    )}
                {user_name && (
                    <InfoRow label={locale.tom.reportedBy} value={user_name} />
                )}
                {reportSeverity && (
                    <InfoRow
                        label={locale.tom.reportType}
                        value={
                            <div className={cls.flexRow}>
                                <span>
                                    {getReportTypeText({ report_type }, locale)}
                                </span>
                                <Icon htmlColor={color} />
                            </div>
                        }
                    />
                )}
                <InfoRow
                    label={locale.tom.dateTime}
                    value={getFormattedDate(new Date(created_at))}
                />
                <InfoRow
                    label={locale.tom.operationMode}
                    value={
                        <OperationModeIcon
                            operationMode={operationMode}
                            locale={locale}
                        />
                    }
                />
                {hasAdditionalTomData &&
                    report_data.tom_data.comment &&
                    report_data.tom_data.comment.replace(/\s/g, '') !== '' && (
                        <InfoRow
                            label={locale.tom.comments}
                            value={report_data.tom_data.comment}
                        />
                    )}
                {imageProcessed ||
                (!isEvaluationRequired &&
                    itemProcessStatus !== ITEM_STATUSES.revise) ? (
                    <>
                        <div className={cls.backdrop} />
                        <Button
                            onClick={() =>
                                setItemProcessStatus(ITEM_STATUSES.revise)
                            }
                            className={cls.reviceBtn}
                        >
                            {notProcessed
                                ? locale.tom.makeReport
                                : locale.tom.reviseReport}
                        </Button>
                    </>
                ) : (
                    <div className={cls.actionsRow}>
                        {canUpdate && (
                            <>
                                {ACTIONS.map(i => (
                                    <DialogOpener
                                        key={i.label}
                                        dialog={(
                                            handleClose,
                                            { onOpen, onClose, open }
                                        ) => (
                                            <Dialog
                                                maxWidth="md"
                                                onEntered={onOpen}
                                                onClose={onClose}
                                                open={open}
                                            >
                                                <ReportForm
                                                    onSubmit={() => {
                                                        onClose()
                                                        setItemProcessStatus(
                                                            ITEM_STATUSES.processed
                                                        )
                                                    }}
                                                    queryKey={queryKey}
                                                    onClose={onClose}
                                                    pictureData={item}
                                                    type={i.severity}
                                                />
                                            </Dialog>
                                        )}
                                    >
                                        {handleOpen => (
                                            <Button
                                                endIcon={
                                                    i.severity ===
                                                    'critical' ? (
                                                        <CriticalIcon />
                                                    ) : (
                                                        <WarningIcon />
                                                    )
                                                }
                                                onClick={handleOpen}
                                                {...i.color}
                                            >
                                                {locale.tom[i.label]}
                                            </Button>
                                        )}
                                    </DialogOpener>
                                ))}
                                <Button
                                    active
                                    endIcon={<OkIcon />}
                                    onClick={handleMakeOkReport}
                                >
                                    {isLoading ? (
                                        <CircularProgress
                                            size={24}
                                            color="inherit"
                                        />
                                    ) : (
                                        locale.tom.ok
                                    )}
                                </Button>
                            </>
                        )}
                    </div>
                )}
                {/* {!isEvaluationRequired && itemProcessStatus !== ITEM_STATUSES.processed && itemProcessStatus !== ITEM_STATUSES.revise && !(!notProcessed) && (
                    <Button
                        onClick={() => setIsEvaluationRequired(true)}
                        className={cls.reviceBtn}
                    >
                        {locale.tom.makeReport}
                    </Button>
                )} */}
                {/* {areActionsVisible && (

                )} */}
            </div>
        </div>
    )
}

const ACTIONS = [
    {
        color: {
            warning: true
        },
        label: 'warning',
        severity: 'warning'
    },
    {
        color: {
            error: true
        },
        label: 'critical',
        severity: 'critical'
    }
]

const InfoRow = ({ label, value }) => {
    const cls = useStyles()

    return (
        <div className={cls.detailsRow}>
            <div className={cls.detailsNameColumn}>{label}</div>
            <div className={cls.detailsValueColumn}>{value}</div>
        </div>
    )
}
